"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, Flex, Separator, Switch, Text, TextArea } from "@radix-ui/themes";
import { budgetLinePrice, resolveSmsTemplatePlaceholders } from "@/lib/util";
import type { TransitionStateBody } from "@/services/engagement";

export type TransitionStateEngagement = {
  offerToken?: string;
  creatorPriceId: number;
  customPrice?: number | null;
  outreachChannel?: "Sms" | "Instagram";
  recipientIgScopedId?: string | null;
  creatorPrice: {
    username?: string;
    platform?: string;
    creator?: { name?: string | null; smsOptIn?: boolean };
  };
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  stateLabel: string;
  creatorName: string;
  username: string;
  hasPhone: boolean;
  smsOptIn: boolean;
  newState: string;
  campaign: Campaign;
  engagement: TransitionStateEngagement | null;
  templates: any[] | undefined;
  transitioning: boolean;
  onConfirm: (smsOptions: Pick<TransitionStateBody, "sendStateSms" | "stateSmsBody"> | undefined) => void | Promise<void>;
};

export default function TransitionStateConfirmDialog({
  open,
  onOpenChange,
  title,
  stateLabel,
  creatorName,
  username,
  hasPhone,
  smsOptIn,
  newState,
  campaign,
  engagement,
  templates,
  transitioning,
  onConfirm,
}: Props) {
  const [smsDraft, setSmsDraft] = useState("");
  const [sendSms, setSendSms] = useState(true);

  const isInstagram = engagement?.outreachChannel === "Instagram";
  const hasIgRecipient = !!engagement?.recipientIgScopedId;
  const channelLabel = isInstagram ? "Instagram DM" : "SMS";
  const canSendOutreach = isInstagram ? hasIgRecipient : hasPhone && smsOptIn;

  const tmpl = useMemo(
    () => (templates || []).find((t: any) => t.triggerState === newState && t.enabled),
    [templates, newState],
  );

  /** Avoid wiping edits when SWR replaces `engagement` with a new object reference. */
  const draftInitMark = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      draftInitMark.current = null;
      return;
    }
    if (!engagement) {
      setSmsDraft("");
      setSendSms(false);
      return;
    }
    const mark = [
      engagement.creatorPriceId,
      engagement.offerToken ?? "",
      engagement.customPrice ?? "",
      newState,
      tmpl?.id ?? "",
      hasPhone,
      smsOptIn,
      isInstagram,
      hasIgRecipient,
      campaign.id,
    ].join("|");
    if (draftInitMark.current === mark) return;
    draftInitMark.current = mark;

    const baseUrl = (process.env.NEXT_PUBLIC_DOMAIN || "").replace(/\/$/, "");
    const offerToken = engagement.offerToken ?? "";
    const offerLink = offerToken ? `${baseUrl}/offer/${offerToken}` : `${baseUrl}/offer/`;
    const vars = {
      creatorName: engagement.creatorPrice?.creator?.name ?? "",
      campaignName: campaign.name ?? "",
      offerLink,
      platform: engagement.creatorPrice?.platform ?? "",
      price: String(
        budgetLinePrice(campaign, engagement.creatorPriceId, engagement.customPrice),
      ),
    };
    if (tmpl && canSendOutreach) {
      setSmsDraft(resolveSmsTemplatePlaceholders(tmpl.body, vars));
      setSendSms(true);
    } else {
      setSmsDraft("");
      setSendSms(false);
    }
  }, [open, engagement, tmpl, canSendOutreach, campaign, newState]);

  const buildSmsOptions = (): Pick<TransitionStateBody, "sendStateSms" | "stateSmsBody"> | undefined => {
    if (!canSendOutreach) return undefined;
    if (!tmpl) return undefined;
    if (!sendSms) return { sendStateSms: false };
    const trimmed = smsDraft.trim();
    // If the box was cleared, treat it as "skip SMS" rather than silently falling
    // back to the server template — the user deleted the message intentionally.
    if (trimmed.length === 0) return { sendStateSms: false };
    return { stateSmsBody: trimmed };
  };

  const handleConfirm = async () => {
    await onConfirm(buildSmsOptions());
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="3">
          Set <strong>{creatorName}</strong> (@{username}) to <strong>{stateLabel}</strong>.
        </Dialog.Description>
        <Flex direction="column" gap="3">
          {canSendOutreach ? (
            tmpl ? (
              <>
                <Flex align="center" gap="2">
                  <Switch checked={sendSms} onCheckedChange={(v) => setSendSms(v === true)} />
                  <Text size="2">Send {channelLabel} for this state change</Text>
                </Flex>
                {isInstagram && (
                  <Text size="1" color="gray">
                    Meta limits cold outreach to 1 tagged message per creator per 7 days. Replies
                    open the standard 24h window.
                  </Text>
                )}
                <TextArea
                  rows={5}
                  value={smsDraft}
                  onChange={(e) => setSmsDraft(e.target.value)}
                  placeholder="Message to send…"
                  disabled={!sendSms}
                  style={{ opacity: sendSms ? 1 : 0.6 }}
                />
                <Text size="1" color="gray">
                  Prefilled from your template for this state. Turn off the switch to change state
                  only with no message.
                </Text>
              </>
            ) : (
              <Text size="2" color="orange">
                No enabled template for this state — no message will be sent.
              </Text>
            )
          ) : (
            <Text size="2" color="gray">
              {isInstagram
                ? "No Instagram recipient on file — configure IG outreach first."
                : !hasPhone
                  ? "No phone on file — no SMS."
                  : "Creator not opted in — no SMS."}
            </Text>
          )}
          <Separator size="4" />
          <Flex gap="2" justify="end">
            <Button variant="soft" color="gray" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={transitioning}
              color={newState === "Canceled" || newState === "Declined" ? "red" : undefined}
            >
              {transitioning ? "Saving…" : "Confirm"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
